import { RedoOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { getLoginCount, getPartners } from 'api';
import { ButtonTextIcon, LineVertical, Pagination } from 'components';
import Breadcrumb from 'components/Layout/ApplicationLayout/Breadcrumb';
import { DATE_FORMAT_LIST } from 'constant/notificationConstants';
import {
  defaultPageSizeOptions,
  defaultSelectPageSizeOption,
} from 'constant/Pagination/pageSizeOption';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import useErrorNetworkMessage from 'hook/useErrorNetworkMessage';
import useCustomQuery from 'hook/useQuery/useCustomQuery';
import useSetTitlePage from 'hook/useSetTitlePage';
import FormDatePicker from 'page/Application/VisitedPage/component/DatePicker';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ButtonOkStyle } from '../ManageCompanyList/component/Form/styled';
import {
  AccountAccessRanking,
  FileDownloadRanking,
  HeaderListDataLayout,
  LabelSelectProduct,
  LayoutDefault,
  LoginCount,
  TableGroup,
} from './components';
import useMultiLanguageEnterpriseLog from './hook/useMultiLanguageEnterpriseLog';

dayjs.extend(isSameOrAfter);

const EnterpriseLog = () => {
  const { useQueryAPI, useQueryWithoutParams } = useCustomQuery();
  const { isOnline, errorNetWork, contextErrorNetWork } =
    useErrorNetworkMessage();

  const defaultDateFrom = dayjs().subtract(30, 'day');
  const defaultDateTo = dayjs();

  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
  });
  const [partnerId, setPartnerId] = useState(null);
  const [countToReset, setCountToReset] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filterData, setFilterData] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    partnerId: null,
  });
  const [isAccountsTableLoading, setIsAccountsTableLoading] = useState(false);
  const [isFilesDownloadTableLoading, setIsFilesDownloadTableLoading] =
    useState(false);
  const [emptyText, setEmptyText] = useState('データがありません');

  const { ENTERPRISE_LOG_LIST_TITLE } = useMultiLanguageEnterpriseLog();

  const listItem = useMemo(() => {
    return [
      {
        title: <Link to={'/'}>企業管理</Link>,
      },
      {
        title: ENTERPRISE_LOG_LIST_TITLE,
      },
    ];
  }, []);

  const { data: loginCount, isLoading: isLoadingLoginCount } = useQueryAPI({
    api: getLoginCount,
    key: 'getLoginCount',
    params: {
      dateFrom: filterData.dateFrom.format('YYYY-MM-DD'),
      dateTo: filterData.dateTo.format('YYYY-MM-DD'),
      id: filterData?.partnerId,
    },
    enabled:
      filterData?.partnerId !== null && filterData?.partnerId !== undefined,
  });

  const { data: partners } = useQueryWithoutParams({
    api: getPartners,
    key: 'getPartners',
  });

  const handleChangePageSize = (value) => {
    if (isOnline) {
      setPageSize(value);
    } else {
      errorNetWork();
    }
  };

  const handleChangeDateRange = (values) => {
    if (isOnline) {
      if (values && values.length === 2) {
        const dateFrom = values[0] ? values[0] : '';
        const dateTo = values[1] ? values[1] : '';
        if (dayjs(dateFrom).isAfter(dayjs(dateTo))) {
          setDateRange({ dateFrom: dateTo, dateTo: dateFrom });
        } else {
          setDateRange({ dateFrom, dateTo });
        }
      } else {
        setDateRange({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
      }
    } else {
      errorNetWork();
    }
  };

  const handleFilterData = () => {
    try {
      if (isOnline) {
        if (partnerId !== null) {
          setFilterData({
            ...dateRange,
            partnerId,
          });
          setEmptyText('データが見つかりませんでした。');
          setCountToReset((prev) => prev + 1);
        }
      } else {
        errorNetWork();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleResetData = () => {
    if (isOnline) {
      setDateRange({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
      setPartnerId(null);
      setPageSize(10);
      setCountToReset((prev) => prev + 1);
      setFilterData({
        dateFrom: defaultDateFrom,
        dateTo: defaultDateTo,
        partnerId: null,
      });
      setEmptyText('データがありません');
    } else {
      errorNetWork();
    }
  };

  const handleSelectPartner = (value) => {
    setPartnerId(value);
  };

  const handleSearch = (value) => {
    if (value && value.length > 256) {
      setSearchText(value.slice(0, 256));
      return value.slice(0, 256);
    }
    setSearchText(value);
    return value;
  };

  useSetTitlePage(
    `${ENTERPRISE_LOG_LIST_TITLE} | Soliton パートナーサイト`,
    true,
  );

  return (
    <>
      {contextErrorNetWork}
      <Breadcrumb listItem={listItem} titlePage={ENTERPRISE_LOG_LIST_TITLE} />
      <LayoutDefault>
        <HeaderListDataLayout
          value={pageSize}
          handleChangePageSize={handleChangePageSize}
          pageSizeOptions={defaultSelectPageSizeOption}
          titleScreen={ENTERPRISE_LOG_LIST_TITLE}
          disabled={
            isLoadingLoginCount ||
            isAccountsTableLoading ||
            isFilesDownloadTableLoading
          }
        >
          <>
            <ButtonTextIcon
              text={'リフレッシュ'}
              icon={<RedoOutlined />}
              onClick={handleResetData}
              disabled={
                isLoadingLoginCount ||
                isAccountsTableLoading ||
                isFilesDownloadTableLoading
              }
            />
            <LineVertical />
            <LabelSelectProduct>会社名</LabelSelectProduct>
            <Select
              showSearch
              placeholder="会社名を入力してください。"
              optionFilterProp="label"
              onChange={handleSelectPartner}
              onSearch={handleSearch}
              searchValue={searchText}
              options={
                partners?.data?.map((item) => ({
                  value: item.partnerId,
                  label: item.partnerName,
                })) || []
              }
              style={{ width: 300, height: 36 }}
              value={partnerId}
              filterOption={(input, option) => {
                return (
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              disabled={
                isLoadingLoginCount ||
                isAccountsTableLoading ||
                isFilesDownloadTableLoading
              }
            />
            <LineVertical />
            <FormDatePicker
              width={'360px'}
              height={'36px'}
              dateFormat={DATE_FORMAT_LIST[1]}
              isShowTime={false}
              required={false}
              defaultFromDate={dateRange.dateFrom}
              defaultToDate={dateRange.dateTo}
              resetTrigger={countToReset}
              setDateRange={setDateRange}
              listDisable={
                isLoadingLoginCount ||
                isAccountsTableLoading ||
                isFilesDownloadTableLoading
              }
            />
            <LineVertical />
            <ButtonOkStyle
              type="primary"
              htmlType="submit"
              onClick={handleFilterData}
              disabled={
                isLoadingLoginCount ||
                isAccountsTableLoading ||
                isFilesDownloadTableLoading
              }
            >
              {' '}
              検索
            </ButtonOkStyle>
          </>
        </HeaderListDataLayout>
        <LoginCount>
          <span>ログイン数: {loginCount?.data?.toLocaleString() ?? 0}</span>
        </LoginCount>
        <TableGroup>
          <AccountAccessRanking
            filterData={filterData}
            pageSize={pageSize}
            countToReset={countToReset}
            emptyText={emptyText}
            setIsAccountsTableLoading={setIsAccountsTableLoading}
          />
          <FileDownloadRanking
            filterData={filterData}
            pageSize={pageSize}
            countToReset={countToReset}
            emptyText={emptyText}
            setIsFilesDownloadTableLoading={setIsFilesDownloadTableLoading}
          />
        </TableGroup>
        <Pagination
          handleChangePageSize={handleChangePageSize}
          pageSize={pageSize}
          pageSizeOptions={defaultPageSizeOptions}
          isShowTotal={false}
          isShowPagination={false}
        />
      </LayoutDefault>
    </>
  );
};
export default EnterpriseLog;
